import { DeviceResolver } from './device_resolver';
import { MapSearch } from './map_search';
import { CustomMapMarkersCollection } from './custom_marker';

class TextSearchingEngine {
  constructor(mapSearch, showCallback, hideCallback) {
    this.mapSearch = mapSearch;
    this.locations = mapSearch.locations;
    this.showCallback = showCallback;
    this.hideCallback = hideCallback;
    this.mapSearch.synchronizeWith(this);
  }

  call(filters) {
    this.locations.forEach((location) => {
      if (this.mapSearch.runChecking(filters, location)) {
        this.showCallback(location.id);
      } else {
        this.hideCallback(location.id);
      }
    });
  }
}

class Map {

  constructor(mapElement, locations, localVendorOrientation) {
    this.moveToExactLocation = this.moveToExactLocation.bind(this);
    this.moveToPlaceLocation = this.moveToPlaceLocation.bind(this);
    this.mapElement = mapElement;
    this.locations = locations;
    this.localVendorOrientation = localVendorOrientation;
    this.setMapDimensions(mapElement);
    this.map = new google.maps.Map(mapElement, {
      zoom: this.initialZoom(),
      center: this.initialLocation(),
      mapTypeControl: true,
      gestureHandling: 'greedy'
    });
    this.googleService = new google.maps.places.AutocompleteService();
    this.placeService = new google.maps.places.PlacesService(this.map);
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    this.map.setClickableIcons(false);
    this.initPostMapActions();
    this.showLocalVendorTooltip();
  }

  setMapDimensions(map) {
    if (DeviceResolver.desktopWidth() && !(DeviceResolver.isMobile())) {
      return;
    }
    const padding = document.querySelector('header').offsetHeight + document.querySelector('.container-fluid > .navbar').offsetHeight;
    map.style.paddingBottom = `calc(var(--vh, 1vh) * 100 - ${padding}px)`;
  }

  showLocalVendorTooltip() {
    if (!this.localVendorOrientation) {
      return;
    }

    const listFilter = document.getElementById('listFilter');
    const vendorFilter = document.querySelector('.vendor-filter');
    const vendorCheckbox = document.getElementById('vendor_filter');

    if (!vendorFilter || !vendorCheckbox) {
      return;
    }

    listFilter.classList.add('show');

    // Initialize Bootstrap popover using vanilla JS
    if (window.bootstrap) {
      const popover = new bootstrap.Popover(vendorFilter, {
        template: "<div class='popover local-vendor' role='tooltip'><div class='arrow'></div><div class='popover-body'></div></div>",
        content: I18n.t('modern.maps.side_form.filtering.local_vendors.tooltip_html'),
        placement: this.localVendorOrientation,
        html: true,
        trigger: 'focus'
      });

      vendorCheckbox.focus();
      vendorCheckbox.addEventListener('blur', () => {
        popover.dispose();
      }, { once: true });
    }
  }

  initPostMapActions() {
    this.markers = new CustomMapMarkersCollection(this.locations, this.map);
    google.maps.event.addListenerOnce(this.map, 'idle', () => this.mapLoadedCallback());
    this.map.addListener('bounds_changed', () => this.mapBoundaryChanged());
  }

  mapLoadedCallback() {
    if (this.mapSearch) {
      return;
    }
    const searchInput = document.getElementById('search');
    if (searchInput.value === '') {
      this.updateLocationBasedOnPosition();
    } else {
      this.centerToSearchedLocation();
    }
    this.mapSearch = new MapSearch(this.locations, 'map');
    this.textSearchEngine = new TextSearchingEngine(this.mapSearch, this.markers.show, this.markers.hide);
    this.mapSearch.activateAutoComplete(
      this.moveToExactLocation,
      this.moveToPlaceLocation,
      this.googleService,
      this.placeService,
      this.sessionToken
    );
  }

  mapBoundaryChanged() {
    this.markers.showLocationsInBox(
      this.map.getBounds().getNorthEast().lat(),
      this.map.getBounds().getNorthEast().lng(),
      this.map.getBounds().getSouthWest().lat(),
      this.map.getBounds().getSouthWest().lng()
    );
    if (this.mapSearch) {
      this.mapSearch.runFilter();
    }
  }

  centerToSearchedLocation() {
    const searchedText = document.getElementById('search').value;
    const exactLocation = this.containsBikeLinkLocation(searchedText);
    if (exactLocation !== null) {
      this.moveToExactLocation(exactLocation.id, exactLocation.latitude, exactLocation.longitude);
    } else {
      this.centerGoogleSearchedLocation(searchedText);
    }
  }

  containsBikeLinkLocation(searchedText) {
    const lowerSearchedText = searchedText.toLowerCase();
    return this.locations.find(location =>
      location.human_name.toLowerCase() === lowerSearchedText
    ) || null;
  }

  centerGoogleSearchedLocation(searchedText) {
    const googleParams = {
      input: searchedText,
      sessionToken: this.sessionToken,
      componentRestrictions: { country: 'us' }
    };
    this.googleService.getPlacePredictions(googleParams, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK) {
        return;
      }
      this.placeService.getDetails(
        { placeId: predictions[0].place_id, fields: ['geometry'] },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            return;
          }
          this.moveToPlaceLocation(place.geometry.location);
        }
      );
    });
  }

  moveToExactLocation(id, latitude, longitude) {
    this.map.setCenter(new google.maps.LatLng(latitude, longitude));
    this.map.setZoom(17);
    this.markers.activate(id);
  }

  moveToPlaceLocation(position) {
    this.map.setCenter(position);
    this.map.setZoom(17);
  }

  initialZoom() {
    if (this.locations.length === 1) {
      return 14;
    }
    return 4;
  }

  initialLocation() {
    if (this.locations.length === 1) {
      return { lat: this.locations[0].latitude, lng: this.locations[0].longitude };
    }
    return { lat: 40.922846, lng: -102.138878 };
  }

  updateLocationBasedOnPosition() {
    if (navigator.geolocation && (this.locations.length > 1)) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.map.setCenter(new google.maps.LatLng(position.coords.latitude, position.coords.longitude));
        this.map.setZoom(8);
      });
    }
  }
}

export { Map };
